<template>
  <div class="statistics-page">
    <div class="toolbar">
      <h1 class="title">Статистика: общая</h1>
      <span v-if="totalRows != null" class="total-rows">всего {{ fmtNum(totalRows) }} рейсов</span>
      <div class="toolbar-actions">
        <button class="btn" type="button" :disabled="csvLoading" @click="onExportCsv">
          {{ csvLoading ? 'Экспорт…' : 'Экспорт CSV' }}
        </button>
        <button class="btn" type="button" :disabled="isRefreshing" @click="refreshAll">
          {{ isRefreshing ? 'Обновление…' : 'Обновить' }}
        </button>
      </div>
    </div>

    <div v-if="forbidden" class="state-block forbidden">
      Нет доступа к разделу статистики (требуется право «Статистика: общая»).
    </div>

    <template v-else>
      <div class="filters-panel">
        <button class="btn btn-reset" type="button" @click="resetFilters">Сбросить</button>
        <FilterPresetsDropdown
          storage-key="statisticsGeneralFilterPresets"
          :current="filtersSnapshot"
          @apply="applyFiltersSnapshot"
        />
        <DatePresetsDropdown
          :date-from="filters.dateFrom"
          :date-to="filters.dateTo"
          :preset-id="filters.presetId"
          :basis="filters.dateBasis"
          timezone-hint
          @change="onPeriodChange"
          @basis-change="filters.dateBasis = $event"
        />
        <MultiDropdown
          class="filter-item"
          label="Логист"
          :options="options.logists"
          :special-options="LOGIST_SPECIALS"
          :model-value="filters.logists"
          @update:model-value="filters.logists = $event"
        />
        <template v-if="filters.linkedCities">
          <MultiDropdown
            class="filter-item"
            label="Город (отправка или прибытие)"
            :options="linkedCityOptions"
            :groups="CITY_GROUPS"
            :model-value="filters.citiesAny"
            @update:model-value="filters.citiesAny = $event"
          />
          <button
            class="link-cities-btn"
            :class="{ active: filters.linkedCities }"
            type="button"
            title="Объединённый поиск: город ищется и в отправке, и в назначении"
            @click="toggleLinkedCities"
          >
            🔗
          </button>
        </template>
        <template v-else>
          <MultiDropdown
            class="filter-item"
            label="Город отправки"
            :options="options.cities_out"
            :groups="CITY_GROUPS"
            :model-value="filters.citiesOut"
            @update:model-value="filters.citiesOut = $event"
          />
          <button
            class="link-cities-btn"
            :class="{ active: filters.linkedCities }"
            type="button"
            title="Объединённый поиск: город ищется и в отправке, и в назначении"
            @click="toggleLinkedCities"
          >
            🔗
          </button>
          <MultiDropdown
            class="filter-item"
            label="Город прибытия"
            :options="options.cities_in"
            :groups="CITY_GROUPS"
            :model-value="filters.citiesIn"
            @update:model-value="filters.citiesIn = $event"
          />
        </template>
        <MultiDropdown
          class="filter-item"
          label="Сегмент"
          :options="options.segments"
          :model-value="filters.segments"
          @update:model-value="filters.segments = $event"
        />
        <MultiDropdown
          class="filter-item"
          label="Категория клиента"
          :options="CLIENT_CATEGORY_OPTIONS"
          :model-value="filters.clientCategories"
          @update:model-value="filters.clientCategories = $event"
        />
        <MultiDropdown
          class="filter-item"
          label="Статус"
          :options="statusesOptions"
          :model-value="filters.statuses"
          @update:model-value="filters.statuses = $event"
        />
        <MultiDropdown
          class="filter-item"
          label="Собственность"
          :options="options.execution"
          :model-value="filters.execution"
          @update:model-value="filters.execution = $event"
        />
        <MultiDropdown
          class="filter-item"
          label="Часы до погрузки"
          :options="TIMING_OPTIONS"
          :model-value="filters.closingTiming"
          @update:model-value="filters.closingTiming = $event"
        />
        <select v-model="filters.bot" class="filter-item bot-select" :class="{ active: filters.bot }">
          <option value="">Победитель</option>
          <option value="bot">Только бот</option>
          <option value="human">Кроме бота</option>
        </select>
        <input
          v-model.trim="filters.trip"
          class="filter-item trip-input"
          :class="{ active: filters.trip }"
          type="text"
          placeholder="№ рейса"
        />
      </div>

      <div v-if="snapshotError" class="state-block warn">
        Данные ещё обновляются, повторите попытку через минуту.
      </div>
      <div v-else-if="hasError" class="state-block error">
        Не удалось загрузить данные.
        <button class="btn" type="button" @click="refreshAll">Повторить</button>
      </div>

      <template v-else>
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">Рейсы</div>
            <div class="kpi-value">{{ fmtNum(kpis.trips_total) }}</div>
            <div class="kpi-sub">
              закрыто {{ fmtNum(kpis.closed_total) }} · открыто {{ fmtNum(kpis.opened_total) }} · новых
              {{ fmtNum(kpis.new_total) }}
            </div>
            <div v-if="kpis.bot_wins != null || kpis.human_wins != null" class="kpi-sub">
              бот: {{ fmtNum(kpis.bot_wins) }} · люди: {{ fmtNum(kpis.human_wins) }}
            </div>
            <div v-if="kpis.own_fleet_total != null" class="kpi-sub">
              наш ТС: {{ fmtNum(kpis.own_fleet_total) }}<template v-if="kpis.own_fleet_pct != null">
                ({{ fmtPct(kpis.own_fleet_pct) }})</template>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Комиссия</div>
            <div class="kpi-value">{{ fmtMoney(kpis.total_commission) }}</div>
            <div class="kpi-sub">чистая {{ fmtMoney(kpis.net_commission_total) }}</div>
            <div class="kpi-sub" :class="{ 'kpi-warn': Number(kpis.breakdown_loss_total) > 0 }">
              штрафы за срывы {{ fmtMoney(kpis.breakdown_loss_total) }}
            </div>
            <div v-if="kpis.financial_result_total != null" class="kpi-sub">
              финрезультат {{ fmtMoney(kpis.financial_result_total) }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Средние показатели</div>
            <div class="kpi-value">{{ fmtPct(kpis.avg_margin_pct) }}</div>
            <div class="kpi-sub">маржа</div>
            <div class="kpi-sub">рентабельность {{ fmtPct(kpis.avg_profitability_pct) }}</div>
            <div class="kpi-sub">комиссия {{ fmtMoney(kpis.avg_commission) }}</div>
            <div class="kpi-sub">KPI {{ fmtMoney(kpis.avg_kpi) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Закрытие</div>
            <div class="kpi-value">{{ fmtHoursNum(kpis.avg_hours_to_load_close) }} ч</div>
            <div class="kpi-sub">запас до погрузки при закрытии</div>
            <div class="kpi-sub">поиск {{ fmtHoursNum(kpis.avg_search_sec) }} ч · в поиске сейчас {{ fmtHoursNum(kpis.avg_ongoing_sec) }} ч</div>
            <div class="kpi-sub">
              ≥24 ч: {{ fmtNum(kpis.closed_early_total) }} · 12–24 ч: {{ fmtNum(kpis.closed_lastday_total) }}
              · &lt;12 ч: {{ fmtNum(kpis.closed_late_total) }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Срывы и опоздания</div>
            <div class="kpi-value" :class="{ 'kpi-warn': Number(kpis.breakdown_total) > 0 }">
              {{ fmtNum(kpis.breakdown_total) }} срыв(ов)
            </div>
            <div class="kpi-sub">опоздания: {{ fmtNum(kpis.expired_total) }}</div>
            <div class="kpi-sub" :class="{ 'kpi-warn': Number(kpis.breakdown_loss_total) > 0 }">
              штрафы за срывы: {{ fmtMoney(kpis.breakdown_loss_total) }}
            </div>
            <div v-if="kpis.kpi_fund_diff_ytd != null" class="kpi-sub">
              Фонд Кипиай (с нач. года): {{ fmtMoney(kpis.kpi_fund_diff_ytd) }}
            </div>
          </div>
        </div>

        <div class="tabs">
          <button
            v-for="tab in TABS"
            :key="tab.id"
            class="tab-btn"
            type="button"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="tab-content">
          <TripsTab
            v-show="activeTab === 'trips'"
            :rows="tripsRows"
            :total="tripsTotal"
            :page="tripsPage"
            :page-size="TRIPS_PAGE_SIZE"
            :sorting="tripsSorting"
            :is-loading="tripsQuery.isFetching.value"
            :day-rows="tripsByDayQuery.data.value || []"
            :day-loading="tripsByDayQuery.isFetching.value"
            @sorting-changed="onTripsSorting"
            @page-changed="tripsPage = $event"
          />
          <DynamicsTab
            v-show="activeTab === 'dynamics'"
            v-model:step="step"
            :rows="temporalQuery.data.value || []"
            :is-loading="temporalQuery.isFetching.value"
            :hour-cap-exceeded="hourCapExceeded"
          />
          <LogistsTab
            v-show="activeTab === 'logists'"
            :rows="logistsQuery.data.value || []"
            :is-loading="logistsQuery.isFetching.value"
          />
          <DirectionsTab
            v-show="activeTab === 'directions'"
            :rows="directionsQuery.data.value || []"
            :all-history-rows="directionsAllHistoryQuery.data.value || []"
            :lifecycle-rows="directionLifecycleQuery.data.value || []"
            :min-trips="5"
            :is-loading="directionsQuery.isFetching.value"
            :all-history-loading="directionsAllHistoryQuery.isFetching.value"
            :lifecycle-loading="directionLifecycleQuery.isFetching.value"
            :date-from="filters.dateFrom"
          />
          <SegmentsTab
            v-show="activeTab === 'segments'"
            :rows="segmentsQuery.data.value || []"
            :kpis="kpis"
            :is-loading="segmentsQuery.isFetching.value"
          />
          <StatusesTab
            v-show="activeTab === 'statuses'"
            :summary-rows="statusSummaryQuery.data.value || []"
            :matrix-rows="statusByLogistQuery.data.value || []"
            :kpis="kpis"
            :is-loading="statusSummaryQuery.isFetching.value || statusByLogistQuery.isFetching.value"
          />
          <BotTab
            v-show="activeTab === 'bot'"
            :summary-rows="botSummaryQuery.data.value || []"
            :auction-rows="botAuctionsQuery.data.value || []"
            :is-loading="botSummaryQuery.isFetching.value || botAuctionsQuery.isFetching.value"
          />
          <ConstructorTab
            v-show="activeTab === 'constructor'"
            :filters="filters"
            :filters-key="filtersKey"
            :is-active="activeTab === 'constructor'"
          />
          <TmReportsTab
            v-show="activeTab === 'tm-reports'"
            :filters="filters"
            :filters-key="filtersKey"
            :is-active="activeTab === 'tm-reports'"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import MultiDropdown from './components/MultiDropdown.vue';
import DatePresetsDropdown from './components/DatePresetsDropdown.vue';
import FilterPresetsDropdown from './components/FilterPresetsDropdown.vue';
import TripsTab from './components/TripsTab.vue';
import DynamicsTab from './components/DynamicsTab.vue';
import LogistsTab from './components/LogistsTab.vue';
import DirectionsTab from './components/DirectionsTab.vue';
import SegmentsTab from './components/SegmentsTab.vue';
import StatusesTab from './components/StatusesTab.vue';
import BotTab from './components/BotTab.vue';
import ConstructorTab from './components/ConstructorTab.vue';
import TmReportsTab from './components/TmReportsTab.vue';
import { TRIPS_PAGE_SIZE, useStatisticsPage } from './useStatisticsPage';
import { downloadTripsCsv } from '@/backend/statisticsApi';
import { fmtHoursNum, fmtMoney, fmtNum, fmtPct } from './format';

const TABS = [
  { id: 'trips', name: 'Рейсы' },
  { id: 'dynamics', name: 'Динамика' },
  { id: 'logists', name: 'Логисты' },
  { id: 'directions', name: 'Направления' },
  { id: 'segments', name: 'Сегменты' },
  { id: 'statuses', name: 'Статусы' },
  { id: 'bot', name: 'Автоторги бота' },
  { id: 'constructor', name: 'Конструктор' },
  { id: 'tm-reports', name: 'Отчёты (ТМ)' },
];

// Временной тип по часам до плановой погрузки (для всех заявок):
// закрытые — от закрытия карточки, открытые/сорванные — от текущего момента.
const TIMING_OPTIONS = [
  { value: 'early', label: '≥24 ч до погрузки', color: '#12b76a' },
  { value: 'lastday', label: '12–24 ч до погрузки', color: '#f79009' },
  { value: 'late', label: '<12 ч / после погрузки', color: '#f04438' },
];

// Спец-пункты фильтра логистов: рейсы с менеджером / без менеджера
const LOGIST_SPECIALS = [
  { value: '__any__', label: 'С менеджером' },
  { value: '__none__', label: 'Без менеджера' },
];

// Категория клиента (Mongo client_category с карточки): 'none' — без категории.
// Цвета — как бейджи категорий в распределении.
const CLIENT_CATEGORY_OPTIONS = [
  { value: 'top', label: 'ТОП', color: '#d4a017' },
  { value: 'key', label: 'КА', color: '#007bff' },
  { value: 'regular', label: 'Обычный', color: '#98a2b3' },
  { value: 'none', label: 'Без категории', color: '#d0d5dd' },
];

// Цвета статусов — как серии графика «Статусы» на вкладке Рейсы
const STATUS_COLORS = {
  'Открыт': '#3b82f6',
  'Закрыт': '#12b76a',
  'Отменен': '#98a2b3',
  'Удален': '#d0d5dd',
  'Устарел': '#667085',
  'Срыв': '#f04438',
  'Отказ': '#f79009',
  'Без карточки': '#e4e7ec',
};

// Быстрые группы городов в фильтрах
const CITY_GROUPS = [
  { label: 'Центр', values: ['Клин', 'Иваново', 'Калуга'] },
  { label: 'Сибирь', values: ['Омск', 'Новосибирск'] },
  { label: 'Юг', values: ['Волжский', 'Волгоград'] },
  { label: 'Волга', values: ['Казань', 'Саранск', 'Уфа', 'Ульяновск'] },
];

export default {
  name: 'GeneralStatisticsPage',
  components: { MultiDropdown, DatePresetsDropdown, FilterPresetsDropdown, TripsTab, DynamicsTab, LogistsTab, DirectionsTab, SegmentsTab, StatusesTab, BotTab, ConstructorTab, TmReportsTab },
  setup() {
    const activeTab = ref('trips');

    const {
      filters,
      filtersKey,
      resetFilters,
      applyFiltersSnapshot,
      step,
      hourCapExceeded,
      optionsQuery,
      kpisQuery,
      tripsQuery,
      tripsByDayQuery,
      tripsSorting,
      tripsPage,
      temporalQuery,
      logistsQuery,
      directionsQuery,
      directionsAllHistoryQuery,
      directionLifecycleQuery,
      segmentsQuery,
      statusSummaryQuery,
      statusByLogistQuery,
      botSummaryQuery,
      botByDayQuery,
      botAuctionsQuery,
      refreshAll,
    } = useStatisticsPage(activeTab);

    const csvLoading = ref(false);

    const forbidden = computed(() => {
      const error = optionsQuery.error.value || kpisQuery.error.value || tripsQuery.error.value;
      return Boolean(error && error.response && error.response.status === 403);
    });

    const snapshotError = computed(() => {
      const error = kpisQuery.error.value || tripsQuery.error.value;
      return Boolean(error && error.response && error.response.status === 503);
    });

    const hasError = computed(() => Boolean(kpisQuery.error.value || tripsQuery.error.value));

    const options = computed(
      () =>
        optionsQuery.data.value || {
          logists: [],
          cities_out: [],
          cities_in: [],
          segments: [],
          statuses: [],
          execution: [],
        }
    );
    const totalRows = computed(() => {
      const data = optionsQuery.data.value;
      return data ? data.total_rows : null;
    });

    const kpis = computed(() => kpisQuery.data.value || {});
    // Снапшот фильтров для пользовательских пресетов (plain-копия reactive)
    const filtersSnapshot = computed(() => JSON.parse(JSON.stringify(filters)));
    const tripsRows = computed(() => {
      const data = tripsQuery.data.value;
      return data ? data.rows : [];
    });
    const tripsTotal = computed(() => {
      const data = tripsQuery.data.value;
      return data ? data.total : 0;
    });

    const isRefreshing = computed(() => kpisQuery.isFetching.value || tripsQuery.isFetching.value);

    watch(
      () => [filters.dateFrom, filters.dateTo, filters.logists, filters.citiesOut, filters.citiesIn, filters.segments, filters.statuses, filters.execution, filters.closingTiming],
      () => {
        tripsPage.value = 1;
      },
      { deep: true }
    );

    const onTripsSorting = (sorting) => {
      tripsSorting.value = sorting;
      tripsPage.value = 1;
    };

    const onPeriodChange = ({ dateFrom, dateTo, presetId }) => {
      filters.dateFrom = dateFrom;
      filters.dateTo = dateTo;
      if (presetId) {
        filters.presetId = presetId;
      }
    };

    const toggleLinkedCities = () => {
      filters.linkedCities = !filters.linkedCities;
      tripsPage.value = 1;
    };

    const linkedCityOptions = computed(() => {
      const union = new Set([...(options.value?.cities_out || []), ...(options.value?.cities_in || [])]);
      return Array.from(union).sort((a, b) => String(a).localeCompare(String(b), 'ru'));
    });

    // Статусы с цветовыми точками (как на графике статусов)
    const statusesOptions = computed(() =>
      (options.value?.statuses || []).map((s) => ({ value: s, label: s, color: STATUS_COLORS[s] || '#98a2b3' })),
    );

    const onExportCsv = async () => {
      csvLoading.value = true;
      try {
        await downloadTripsCsv(filters);
      } finally {
        csvLoading.value = false;
      }
    };

    return {
      TABS,
      TIMING_OPTIONS,
      CLIENT_CATEGORY_OPTIONS,
      TRIPS_PAGE_SIZE,
      filters,
      filtersKey,
      resetFilters,
      applyFiltersSnapshot,
      filtersSnapshot,
      step,
      hourCapExceeded,
      activeTab,
      forbidden,
      snapshotError,
      hasError,
      options,
      totalRows,
      kpis,
      tripsRows,
      tripsTotal,
      tripsPage,
      tripsSorting,
      tripsQuery,
      tripsByDayQuery,
      temporalQuery,
      logistsQuery,
      directionsQuery,
      directionsAllHistoryQuery,
      directionLifecycleQuery,
      segmentsQuery,
      statusSummaryQuery,
      statusByLogistQuery,
      botSummaryQuery,
      botByDayQuery,
      botAuctionsQuery,
      isRefreshing,
      csvLoading,
      onTripsSorting,
      onPeriodChange,
      onExportCsv,
      LOGIST_SPECIALS,
      CITY_GROUPS,
      toggleLinkedCities,
      linkedCityOptions,
      statusesOptions,
      refreshAll,
      fmtHoursNum,
      fmtMoney,
      fmtNum,
      fmtPct,
    };
  },
};
</script>

<style scoped>
.statistics-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 20px 24px 40px;
  box-sizing: border-box;
  overflow-y: auto;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.total-rows {
  color: #667085;
  font-size: 13px;
}

.toolbar-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.btn {
  padding: 8px 14px;
  font-size: 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #1d2939;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  background: #f8fafc;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-reset {
  border-color: #f04438;
  color: #d92d20;
}

.btn-reset:hover:not(:disabled) {
  background: #fff3f2;
}

.filters-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.filter-item {
  min-width: 0;
}

.link-cities-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s;
}

.link-cities-btn:hover {
  background: #f8fafc;
}

.link-cities-btn.active {
  background: #eef4ff;
  border-color: #84adff;
  color: #0056b3;
}

.bot-select {
  height: 36px;
  padding: 0 8px;
  font-size: 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #101828;
  box-sizing: border-box;
  cursor: pointer;
}

.bot-select:focus {
  outline: none;
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.bot-select.active {
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.trip-input {
  width: 140px;
  height: 36px;
  box-sizing: border-box;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #101828;
}

.trip-input:focus {
  outline: none;
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.trip-input.active {
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.filter-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-date label {
  font-size: 11px;
  color: #667085;
}

.filter-date input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 13px;
  color: #1d2939;
  background: #fff;
  box-sizing: border-box;
}

.only-new {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #1d2939;
  cursor: pointer;
  user-select: none;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.kpi-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.kpi-label {
  font-size: 12px;
  color: #667085;
  margin-bottom: 6px;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #101828;
  font-variant-numeric: tabular-nums;
}

.kpi-warn {
  color: #d92d20;
}

.kpi-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #98a2b3;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.tab-btn {
  padding: 10px 16px;
  font-size: 14px;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  color: #667085;
  cursor: pointer;
}

.tab-btn.active {
  color: #101828;
  font-weight: 600;
  border-bottom-color: #3b82f6;
}

.tab-content {
  flex: 1;
  min-height: 300px;
}

.state-block {
  padding: 24px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.state-block.forbidden {
  background: #fffcf5;
  border: 1px solid #fef0c7;
  color: #93370d;
}

.state-block.warn {
  background: #fffcf5;
  border: 1px solid #fef0c7;
  color: #93370d;
}

.state-block.error {
  background: #fff3f2;
  border: 1px solid #fecdca;
  color: #b42318;
}
</style>
